/**
 * Standalone browser attribution module; copy this entire file into other services.
 * Canonical source: DigidooTech/website/shared/utils/attribution.ts.
 * Keep the cookie name, domain and version-1 record format consistent across services.
 */
export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'] as const;
export const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'] as const;

export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export type UtmKey = (typeof UTM_KEYS)[number];
export type ClickId = { value: string; capturedAt: number };

export type Attribution = {
  version: 1;
  firstTouch: {
    capturedAt: number;
    landing: { hostname: string; pathname: string };
    referrerHostname?: string;
    utm: Partial<Record<UtmKey, string>>;
    clickIdKeys: ClickIdKey[];
  };
  clickIds: Partial<Record<ClickIdKey, ClickId>>;
};

export type AttributionOptions = {
  /** Omit for a host-only cookie. A configured domain must match the current host. */
  domain?: string;
  cookieName?: string;
  maxAgeDays?: number;
};

export type CaptureResult = {
  data: Attribution | undefined;
  persisted: boolean;
  rejectedParameters: string[];
  reason?: 'unavailable' | 'invalid-domain' | 'unsupported-version' | 'too-large' | 'blocked';
};

export type AttributionClient = {
  capture(): CaptureResult;
  read(): Attribution | undefined;
  clear(): boolean;
};

const GOOGLE_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
// Includes the name and attributes, leaving room below browsers' 4096-byte limits.
const COOKIE_BUDGET = 3800;
const MAX_TEXT_BYTES = 600;
const MAX_ID_BYTES = 1500;
const MAX_PATH_BYTES = 900;
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

function encodedLength(value: string): number {
  try {
    return encodeURIComponent(value).length;
  } catch {
    return Infinity;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isTimestamp(value: unknown, now: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= now + FUTURE_TOLERANCE_MS;
}

function isText(value: unknown, maxBytes: number): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    // eslint-disable-next-line no-control-regex -- Reject control characters in cookie values.
    !/[\u0000-\u001f\u007f]/.test(value) &&
    encodedLength(value) <= maxBytes
  );
}

function isHostname(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 253 && /^[a-z0-9[\]:.-]+$/i.test(value);
}

function belongsTo(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function parseRecord(value: unknown, now: number): Attribution | undefined {
  if (!isObject(value) || value.version !== 1 || !isObject(value.firstTouch) || !isObject(value.clickIds))
    return undefined;
  const first = value.firstTouch;
  if (
    !isTimestamp(first.capturedAt, now) ||
    !isObject(first.landing) ||
    !isHostname(first.landing.hostname) ||
    !isText(first.landing.pathname, MAX_PATH_BYTES) ||
    !first.landing.pathname.startsWith('/') ||
    /[?#]/.test(first.landing.pathname) ||
    (first.referrerHostname !== undefined && !isHostname(first.referrerHostname)) ||
    !isObject(first.utm) ||
    !Array.isArray(first.clickIdKeys) ||
    first.clickIdKeys.length > CLICK_ID_KEYS.length ||
    !first.clickIdKeys.every((key) => CLICK_ID_KEYS.includes(key))
  )
    return undefined;

  const utm: Attribution['firstTouch']['utm'] = {};
  for (const key of UTM_KEYS) {
    if (first.utm[key] === undefined) continue;
    if (!isText(first.utm[key], MAX_TEXT_BYTES)) return undefined;
    utm[key] = first.utm[key];
  }
  const clickIds: Attribution['clickIds'] = {};
  for (const key of CLICK_ID_KEYS) {
    const click = value.clickIds[key];
    if (click === undefined) continue;
    if (!isObject(click) || !isText(click.value, MAX_ID_BYTES) || !isTimestamp(click.capturedAt, now)) return undefined;
    clickIds[key] = { value: click.value, capturedAt: click.capturedAt };
  }
  return {
    version: 1,
    firstTouch: {
      capturedAt: first.capturedAt,
      landing: { hostname: first.landing.hostname, pathname: first.landing.pathname },
      ...(first.referrerHostname ? { referrerHostname: first.referrerHostname } : {}),
      utm,
      clickIdKeys: [...new Set(first.clickIdKeys)] as ClickIdKey[],
    },
    clickIds,
  };
}

/** No globals are read until a method is called, so importing during SSR is safe. */
export function createAttribution(options: AttributionOptions = {}): AttributionClient {
  const cookieName = options.cookieName ?? 'digidoo_attribution_v1';
  const maxAgeDays = options.maxAgeDays ?? 180;
  const domain = options.domain?.trim().toLowerCase().replace(/^\./, '').replace(/\.$/, '');
  if (
    !/^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/.test(cookieName) ||
    cookieName.length > 100 ||
    cookieName.startsWith('__Host-') ||
    cookieName.startsWith('__Http-') ||
    cookieName.startsWith('__Secure-')
  ) {
    throw new TypeError('Use an unprefixed cookie name of at most 100 characters');
  }
  if (!Number.isFinite(maxAgeDays) || maxAgeDays <= 0 || maxAgeDays > 400) {
    throw new RangeError('maxAgeDays must be greater than 0 and at most 400');
  }
  if (
    options.domain !== undefined &&
    (!domain ||
      domain.length > 253 ||
      !/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(domain) ||
      domain.split('.').some((part) => !part || part.length > 63 || part.startsWith('-') || part.endsWith('-')))
  ) {
    throw new TypeError('domain must be a hostname without a scheme, port or path');
  }
  const maxAge = Math.max(1, Math.floor(maxAgeDays * 86400));

  function environment(): { url: URL; doc: Document } | CaptureResult['reason'] {
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined') return 'unavailable';
      const url = new URL(window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'unavailable';
      if (domain && !belongsTo(url.hostname, domain)) return 'invalid-domain';
      return { url, doc: document };
    } catch {
      return 'unavailable';
    }
  }

  function attributes(url: URL, age = maxAge): string {
    return `; Path=/; Max-Age=${age}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${url.protocol === 'https:' ? '; Secure' : ''}`;
  }

  function serialize(data: Attribution, url: URL): string {
    return `${cookieName}=${encodeURIComponent(JSON.stringify(data))}${attributes(url)}`;
  }

  function fits(data: Attribution, url: URL): boolean {
    return serialize(data, url).length <= COOKIE_BUDGET;
  }

  function stored(doc: Document, now: number): { data?: Attribution; unsupported?: boolean; raw?: string } {
    const matches = doc.cookie
      .split(';')
      .map((part) => part.trim())
      .filter((part) => part.startsWith(`${cookieName}=`));
    // A host-only cookie with the same name could shadow the shared cookie.
    // Refuse ambiguous storage instead of choosing a potentially wrong identity.
    if (matches.length > 1) return { unsupported: true };
    const raw = matches[0]?.slice(cookieName.length + 1);
    if (!raw) return {};
    if (raw.length > 4096) return { unsupported: true, raw };
    try {
      const decoded: unknown = JSON.parse(decodeURIComponent(raw));
      if (isObject(decoded) && typeof decoded.version === 'number' && decoded.version > 1) {
        return { unsupported: true, raw };
      }
      if (raw.length > COOKIE_BUDGET) return { raw };
      return { data: parseRecord(decoded, now), raw };
    } catch {
      return { raw };
    }
  }

  function read(): Attribution | undefined {
    const env = environment();
    if (typeof env !== 'object') return undefined;
    try {
      return stored(env.doc, Date.now()).data;
    } catch {
      return undefined;
    }
  }

  function capture(): CaptureResult {
    const rejectedParameters: string[] = [];
    const env = environment();
    if (typeof env !== 'object')
      return { data: undefined, persisted: false, rejectedParameters, reason: env || 'unavailable' };
    const { url, doc } = env;
    let data: Attribution | undefined;
    try {
      const now = Date.now();
      const previous = stored(doc, now);
      if (previous.unsupported)
        return { data: undefined, persisted: false, rejectedParameters, reason: 'unsupported-version' };
      data = previous.data ? { ...previous.data, clickIds: { ...previous.data.clickIds } } : undefined;
      const incoming: Partial<Record<ClickIdKey, string>> = {};
      const presentKeys: ClickIdKey[] = [];
      for (const key of CLICK_ID_KEYS) {
        const value = url.searchParams.get(key);
        if (!value) continue;
        presentKeys.push(key);
        if (isText(value, MAX_ID_BYTES)) incoming[key] = value;
        else rejectedParameters.push(key);
      }

      if (!data) {
        const pathname = isText(url.pathname, MAX_PATH_BYTES) ? url.pathname : '/';
        if (pathname !== url.pathname) rejectedParameters.push('landing.pathname');
        data = {
          version: 1,
          firstTouch: {
            capturedAt: now,
            landing: { hostname: url.hostname, pathname },
            utm: {},
            clickIdKeys: presentKeys,
          },
          clickIds: {},
        };
        try {
          const referrer = new URL(doc.referrer);
          if (
            (referrer.protocol === 'https:' || referrer.protocol === 'http:') &&
            !belongsTo(referrer.hostname, domain ?? url.hostname)
          ) {
            data.firstTouch.referrerHostname = referrer.hostname;
          }
        } catch {
          /* Missing or suppressed referrer is unknown. */
        }
        for (const key of UTM_KEYS) {
          const value = url.searchParams.get(`utm_${key}`);
          if (!value) continue;
          if (!isText(value, MAX_TEXT_BYTES)) {
            rejectedParameters.push(`utm_${key}`);
            continue;
          }
          data.firstTouch.utm[key] = value;
          if (!fits(data, url)) {
            delete data.firstTouch.utm[key];
            rejectedParameters.push(`utm_${key}`);
          }
        }
      }

      // Replace Google's observation as a whole only if a new identifier appears.
      // A forwarded subset of existing identifiers is not a new click.
      const newGoogleClick = GOOGLE_KEYS.some((key) => incoming[key] && incoming[key] !== data!.clickIds[key]?.value);
      if (newGoogleClick) {
        const oldGoogle: Partial<Record<(typeof GOOGLE_KEYS)[number], ClickId>> = Object.fromEntries(
          GOOGLE_KEYS.flatMap((key) => (data!.clickIds[key] ? [[key, data!.clickIds[key]]] : [])),
        );
        for (const key of GOOGLE_KEYS) delete data.clickIds[key];
        for (const key of GOOGLE_KEYS) {
          if (incoming[key])
            data.clickIds[key] = {
              value: incoming[key]!,
              capturedAt: oldGoogle[key]?.value === incoming[key] ? oldGoogle[key].capturedAt : now,
            };
        }
        if (GOOGLE_KEYS.some((key) => rejectedParameters.includes(key)) || !fits(data, url)) {
          for (const key of GOOGLE_KEYS) delete data.clickIds[key];
          Object.assign(data.clickIds, oldGoogle);
          rejectedParameters.push(...GOOGLE_KEYS.filter((key) => incoming[key]));
        }
      }
      for (const key of ['fbclid', 'msclkid', 'ttclid'] as const) {
        const value = incoming[key];
        if (!value || data.clickIds[key]?.value === value) continue;
        const old = data.clickIds[key];
        data.clickIds[key] = { value, capturedAt: now };
        if (!fits(data, url)) {
          if (old) data.clickIds[key] = old;
          else delete data.clickIds[key];
          rejectedParameters.push(key);
        }
      }
      const rejected = [...new Set(rejectedParameters)];
      if (!fits(data, url))
        return { data: previous.data, persisted: false, rejectedParameters: rejected, reason: 'too-large' };
      const cookie = serialize(data, url);
      doc.cookie = cookie;
      // Browsers may silently reject a cookie assignment. Compare raw bytes to
      // detect failed writes; reading does not renew or mutate attribution.
      const verified = stored(doc, now);
      const persisted = verified.raw === cookie.slice(cookieName.length + 1, cookie.indexOf(';'));
      return {
        data: verified.data,
        persisted,
        rejectedParameters: rejected,
        ...(persisted ? {} : { reason: 'blocked' as const }),
      };
    } catch {
      return {
        data: read(),
        persisted: false,
        rejectedParameters: [...new Set(rejectedParameters)],
        reason: 'blocked',
      };
    }
  }

  function clear(): boolean {
    const env = environment();
    if (typeof env !== 'object') return false;
    try {
      env.doc.cookie = `${cookieName}=${attributes(env.url, 0)}`;
      // Also clear a prior host-only cookie if this host is now using a domain cookie.
      if (domain)
        env.doc.cookie = `${cookieName}=; Path=/; Max-Age=0; SameSite=Lax${env.url.protocol === 'https:' ? '; Secure' : ''}`;
      const remaining = stored(env.doc, Date.now());
      return !remaining.raw && !remaining.unsupported;
    } catch {
      return false;
    }
  }

  return { capture, read, clear };
}

// App configuration; keep the implementation above synchronized with the website.
const hostname = typeof window === 'undefined' ? '' : window.location.hostname;

/** Shared with the website; each operation rereads the browser cookie. */
export const attribution = createAttribution({
  domain: hostname === 'digidoo.com' || hostname.endsWith('.digidoo.com') ? 'digidoo.com' : undefined,
  cookieName: 'digidoo_attribution_v1',
  maxAgeDays: 180,
});
